import React from "react";
import { Ship, Radio, Navigation } from "lucide-react";
import { distanceNm, bearingDeg, courseToText } from "@/utils/geo";

export const NEARBY_RADII = [5, 10, 20, 30, 40, 50];
const CRUISE_KN = 25; // velocità stimata per l'ETA se l'unità è ferma

// Calcola le unità (motovedette piattaforma + navi AIS di servizio) entro radiusNm dall'evento.
export function computeNearby(event, motovedette, vessels, radiusNm) {
  const last = event && event.last;
  if (!last) return [];
  const out = [];
  (motovedette || []).forEach((mv) => {
    const p = mv.last_pos;
    if (!p || p.lat == null) return;
    const d = distanceNm(p.lat, p.lng, last.lat, last.lng);
    if (d > radiusNm) return;
    out.push({ key: `mv-${mv.id}`, kind: "mv", id: mv.id, name: mv.username, sub: mv.zone_name,
      lat: p.lat, lng: p.lng, dist: d, bearing: bearingDeg(p.lat, p.lng, last.lat, last.lng),
      speed: p.speed || 0, busy: !!mv._assignedTo, busyLabel: mv._assignedTo, here: !!mv._here });
  });
  (vessels || []).forEach((v) => {
    if (v.category !== "service" || v.lat == null) return;
    const d = distanceNm(v.lat, v.lng, last.lat, last.lng);
    if (d > radiusNm) return;
    out.push({ key: `ais-${v.mmsi}`, kind: "ais", id: v.mmsi, name: v.name || `MMSI ${v.mmsi}`,
      sub: [v.type, `MMSI ${v.mmsi}`].filter(Boolean).join(" · "),
      lat: v.lat, lng: v.lng, dist: d, bearing: bearingDeg(v.lat, v.lng, last.lat, last.lng), speed: v.sog || 0 });
  });
  return out.sort((a, b) => a.dist - b.dist);
}

function eta(dist, speed) {
  const kn = speed > 0.5 ? speed : CRUISE_KN;
  const min = Math.round((dist / kn) * 60);
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, "0")}`;
}

export function NearbyUnits({ units, radiusNm, onRadiusChange, onAssign, onHover, canAssign }) {
  return (
    <div className="pt-3 pb-1" data-testid="nearby-units">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-wide font-heading font-bold text-[#A3C2E0]">Unità vicine</span>
        <select value={radiusNm} onChange={(e) => onRadiusChange(Number(e.target.value))} data-testid="nearby-radius"
          className="bg-[#0A2647] border border-[#1E5AA8] text-white text-[11px] rounded-sm px-1.5 py-0.5 font-mono">
          {NEARBY_RADII.map((r) => <option key={r} value={r}>{r} NM</option>)}
        </select>
      </div>
      {units.length === 0 ? (
        <p className="text-xs text-[#A3C2E0]" data-testid="nearby-empty">Nessuna unità entro {radiusNm} NM.</p>
      ) : (
        <ul className="space-y-1 max-h-48 overflow-y-auto" data-testid="nearby-list">
          {units.map((u) => (
            <li key={u.key} data-testid={`nearby-${u.key}`}
              onMouseEnter={() => onHover && onHover(u)} onMouseLeave={() => onHover && onHover(null)}
              className="bg-[#0A2647] border border-[#1E5AA8]/60 rounded-sm px-2 py-1.5 text-xs text-white">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 min-w-0 font-semibold truncate">
                  {u.kind === "mv" ? <Ship className="h-3.5 w-3.5 text-[#0EA5E9] shrink-0" /> : <Radio className="h-3.5 w-3.5 text-[#3b82f6] shrink-0" />}
                  <span className="truncate">{u.name}</span>
                </span>
                <span className="font-mono shrink-0">{u.dist.toFixed(1)} NM</span>
              </div>
              <div className="flex items-center justify-between gap-2 text-[10px] text-[#A3C2E0] mt-0.5">
                <span className="truncate">{u.kind === "mv" ? (u.here ? "Assegnata a questo evento" : (u.busy ? `Assegnata #${u.busyLabel}` : "Motovedetta · libera")) : (u.sub || "AIS servizio · VHF 16")}</span>
                <span className="font-mono shrink-0 flex items-center gap-1">
                  <Navigation className="h-3 w-3" /> {Math.round(u.bearing)}° {courseToText(u.bearing)} · {eta(u.dist, u.speed)}
                </span>
              </div>
              {u.kind === "mv" && canAssign && !u.busy && (
                <button type="button" onClick={() => onAssign(u.id)} data-testid={`nearby-assign-${u.id}`}
                  className="mt-1 w-full text-[11px] font-heading font-bold border border-[#1E5AA8] rounded-sm py-0.5 hover:bg-[#1E5AA8] transition-colors">
                  Assegna
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
